import { ArrowRightLeft } from "lucide-react";
import { useDataProvider, useGetList, useNotify, useTranslate } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { NevoraDataProvider } from "../datos/dataProvider";
import type { Comercial, Negocio } from "../dominio/tipos";

const nombreDe = (u: Comercial) =>
  `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();

/**
 * «Pasar sus fichas a…» (auditoría 30/10/2026): si un setter se va o está de baja, todas las
 * fichas que lleva pasan de golpe a otra persona, con la misma función que asigna a mano
 * (`nevora_asignar`, solo administradores; queda en el historial de asignaciones).
 */
export const PasarFichas = ({ usuario }: { usuario: Comercial }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const [destino, setDestino] = useState("");
  const [pasando, setPasando] = useState(false);
  const { data: fichas, refetch } = useGetList<Negocio>("negocios", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "id", order: "ASC" },
    filter: { asignado_a: usuario.id },
  });
  const { data: usuarios } = useGetList<Comercial>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });
  const otros = (usuarios ?? []).filter(
    (u) => !u.disabled && u.id !== usuario.id,
  );
  const ids = [
    ...new Set((fichas ?? []).map((f) => Number(f.negocio_id ?? f.id))),
  ];

  const pasar = async () => {
    if (!destino || !ids.length) return;
    setPasando(true);
    try {
      const n = await dataProvider.asignar(ids, Number(destino));
      notify("nevora.en_bloque.pasadas", {
        type: "success",
        messageArgs: { smart_count: n },
      });
      setDestino("");
      await refetch();
    } catch (e) {
      notify(e instanceof Error ? e.message : "ra.notification.http_error", {
        type: "error",
      });
    } finally {
      setPasando(false);
    }
  };

  return (
    <div
      className="space-y-1.5 border-t border-border/60 pt-3"
      data-testid="pasar-fichas"
    >
      <p className="text-xs text-muted-foreground">
        {ids.length
          ? translate("nevora.en_bloque.pasar_todo_ayuda", {
              nombre: nombreDe(usuario),
            })
          : translate("nevora.en_bloque.sin_fichas")}
      </p>
      {ids.length ? (
        <div className="flex flex-wrap items-center gap-2">
          <Select value={destino} onValueChange={setDestino}>
            <SelectTrigger
              className="h-8 w-52"
              aria-label={translate("nevora.en_bloque.pasar_todo")}
            >
              <SelectValue
                placeholder={translate("nevora.en_bloque.pasar_todo")}
              />
            </SelectTrigger>
            <SelectContent>
              {otros.map((u) => (
                <SelectItem key={String(u.id)} value={String(u.id)}>
                  {nombreDe(u)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!destino || pasando}
            onClick={pasar}
          >
            <ArrowRightLeft className="size-4" aria-hidden />
            {translate("nevora.en_bloque.pasar", { smart_count: ids.length })}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
